import Calendar from 'react-calendar';
import { useEffect, useRef, useState } from "react";
import style from "./DateRangePicker.module.css";
import IconEditGoogle from "../../icons/IconEditGoogle";
import IconCheck from "../../icons/IconCheck";
import dayjs from 'dayjs';
import { DATE_RANGE_PRESETS, DateRangeValue, isSameDay } from '@/utils/dateUtils';
import Flyout from '@/components/Flyout';

type CalendarDateValuePiece = Date | null;

type CalendarDateValue = CalendarDateValuePiece | [CalendarDateValuePiece, CalendarDateValuePiece];

interface DateRangePickerProps {
  defaultValue?: DateRangeValue;
  onSelectDateRange?: (dateRange: DateRangeValue) => void;
}

export default function DateRangePicker({ defaultValue, onSelectDateRange }: DateRangePickerProps) {
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeValue | null>(null);
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState<boolean>(false);
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [calendarOrientation, setCalendarOrientation] = useState<'left' | 'right'>('right');

  const dateRangePickerRef = useRef<HTMLDivElement>(null);
  const dateRangePickerContentRef = useRef<HTMLDivElement>(null);

  const handleTriggerButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (dateRangePickerRef.current) {
      const dateRangePickerRect = dateRangePickerRef.current.getBoundingClientRect();
      if (dateRangePickerRect.left > (Math.floor(window.innerWidth / 2))) {
        calendarOrientation !== 'left' && setCalendarOrientation('left');
      } else {
        calendarOrientation !== 'right' && setCalendarOrientation('right');
      }
    }
    setDateRangePickerOpen(!dateRangePickerOpen);
  }

  const handleDateRangeValueSelect = (dateRangePreset: DateRangeValue) => {
    setSelectedDateRange(dateRangePreset);
    onSelectDateRange?.(dateRangePreset);
    setCalendarOpen(false);
    setDateRangePickerOpen(false);
  }

  const handleCalendarDateChange = (dateValue: CalendarDateValue) => {
    const calendarDateLabel = 'Custom range';
    if (dateValue instanceof Date) {
      setSelectedDateRange({ label: calendarDateLabel, from: dateValue });
      onSelectDateRange?.({ label: calendarDateLabel, from: dateValue });
    } else if (dateValue instanceof Array) {
      setSelectedDateRange({ label: calendarDateLabel, from: dateValue[0], to: dateValue[1] });
      onSelectDateRange?.({ label: calendarDateLabel, from: dateValue[0], to: dateValue[1] });
      setDateRangePickerOpen(false);
    }
  }

  useEffect(() => {
    const handleClickOutsideDateRangePicker = (e: MouseEvent) => {
      const outsideButton = dateRangePickerRef.current && !dateRangePickerRef.current.contains(e.target as Node);
      const outsideFlyout = dateRangePickerContentRef.current && !dateRangePickerContentRef.current.contains(e.target as Node);
      if (outsideButton && outsideFlyout) {
        setDateRangePickerOpen(false);
      }
    }

    if (window) {
      window.addEventListener("mousedown", handleClickOutsideDateRangePicker);
      return () => {
        window.removeEventListener("mousedown", handleClickOutsideDateRangePicker);
      }
    }
  }, []);

  const displayedDateRange = selectedDateRange || defaultValue || DATE_RANGE_PRESETS[0];
  const triggerButtonLabel = [dayjs(displayedDateRange.from).format('MMM D'), displayedDateRange.to && !isSameDay(displayedDateRange.from, displayedDateRange.to) && dayjs(displayedDateRange.to).format('MMM D')].filter(Boolean).join(' - ');

  const isPresetSelected = (preset: DateRangeValue) => {
    return displayedDateRange.from.getTime() === preset.from.getTime() && displayedDateRange.to?.getTime() === preset.to?.getTime() && displayedDateRange.label === preset.label;
  }
  const isAnyPresetSelected = () => {
    return DATE_RANGE_PRESETS.some((preset) => isPresetSelected(preset));
  }

  const isCustomRangeSelected = !isAnyPresetSelected() || calendarOpen;

  return (
    <div className={style.dateRangePicker} ref={dateRangePickerRef}>
      <button className={style.triggerButton} onClick={handleTriggerButtonClick}><span className={style.triggerButtonText}>{triggerButtonLabel}</span><IconEditGoogle /></button>

      {dateRangePickerOpen && (
        <Flyout className={style.content} ref={dateRangePickerContentRef} matchWidth={false}>
          <div className={style.presets}>
            <ul className={style.presetsList}>
              {DATE_RANGE_PRESETS.map((preset) => (
                <li key={preset.label} className={style.presetsListItem}>
                  <button
                    className={isPresetSelected(preset) && !calendarOpen ? style.presetButtonSelected : style.presetButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateRangeValueSelect(preset);
                    }}
                  >
                    <span className={style.presetButtonText}>{preset.label}</span>{isPresetSelected(preset) && !calendarOpen && <IconCheck />}
                  </button>
                </li>
              ))}

              <li className={style.presetsListItem}>
                <button
                  className={isCustomRangeSelected ? style.presetButtonSelected : style.presetButton}
                  onClick={() => {
                    setCalendarOpen(true);
                  }}
                >
                  <span className={style.presetButtonText}>Custom range</span>{isCustomRangeSelected && <IconCheck />}
                </button>
              </li>
            </ul>
          </div>

          {isCustomRangeSelected && (
            <div className={`${style.calendar} ${calendarOrientation === 'left' ? style.leftCalendar : null}`}>
              <Calendar value={[displayedDateRange.from, displayedDateRange.to]} onChange={handleCalendarDateChange} selectRange />
              <div className={style.calendarDescription}>Based on Pacific Time zone.</div>
            </div>
          )}
        </Flyout>
      )}
    </div>
  );
}